import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { BrandLogo } from './BrandLogo';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  LayoutDashboard,
  CreditCard,
  FileText,
  Settings,
  Users,
  Shield,
  AlertTriangle,
  FileBarChart,
  LogOut,
  Sun,
  Moon,
  ChevronDown,
  Key,
  Webhook,
  PiggyBank,
  ClipboardList,
  Building2,
  Wallet,
  Send,
  UserCheck,
  Plus,
  History,
  ArrowUpCircle,
  CheckCircle,
  Route,
  Coins,
  Receipt,
  Menu,
  Palette,
  Layers,
  Percent,
  Handshake,
  Store,
  Banknote,
  Bell,
} from 'lucide-react';

const merchantNavGroups = [
  { group: 'Overview', items: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  ]},
  { group: 'Wallet', items: [
    { to: '/dashboard/wallet', icon: Wallet, label: 'Wallet' },
    { to: '/dashboard/wallet/recharge', icon: ArrowUpCircle, label: 'Recharge Wallet' },
  ]},
  { group: 'Payouts', items: [
    { to: '/dashboard/payouts', icon: Send, label: 'Payouts' },
    { to: '/dashboard/payouts/create', icon: Plus, label: 'Create Payout' },
    { to: '/dashboard/beneficiaries', icon: UserCheck, label: 'Beneficiaries' },
  ]},
  { group: 'Transactions', items: [
    { to: '/dashboard/transactions', icon: CreditCard, label: 'Transactions' },
    { to: '/dashboard/settlements', icon: PiggyBank, label: 'Settlements' },
  ]},
  { group: 'Developer', items: [
    { to: '/dashboard/api-config', icon: Key, label: 'API Configuration' },
  ]},
  { group: 'Account', items: [
    { to: '/kyc', icon: FileText, label: 'KYC Verification' },
  ]},
];

const adminNavGroups = [
  { group: 'Overview', items: [
    { to: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
  ]},
  { group: 'Merchant Operations', items: [
    { to: '/admin/merchants', icon: Building2, label: 'Merchants' },
    { to: '/admin/kyc-review', icon: FileText, label: 'KYC Review' },
  ]},
  { group: 'Wallet & Funding', items: [
    { to: '/admin/funding', icon: Coins, label: 'USDT Funding' },
    { to: '/admin/wallet-ledger', icon: Receipt, label: 'Wallet Ledger' },
  ]},
  { group: 'Payout Operations', items: [
    { to: '/admin/payouts', icon: Send, label: 'Payouts' },
    { to: '/admin/payouts/pending', icon: CheckCircle, label: 'Pending Approval' },
    { to: '/admin/bulk-payouts', icon: Layers, label: 'Bulk Payouts' },
  ]},
  { group: 'Payment Infrastructure', items: [
    { to: '/admin/routing', icon: Route, label: 'Multi-Bank Routing' },
    { to: '/admin/transactions', icon: CreditCard, label: 'Transactions' },
    { to: '/admin/settlements', icon: PiggyBank, label: 'Settlements' },
    { to: '/admin/reconciliation', icon: ClipboardList, label: 'Reconciliation' },
  ]},
  { group: 'Risk & Compliance', items: [
    { to: '/admin/risk-management', icon: Shield, label: 'Risk Management' },
  ]},
  { group: 'Partners', items: [
    { to: '/admin/resellers', icon: Handshake, label: 'Resellers' },
    { to: '/admin/reseller-commissions', icon: Coins, label: 'Reseller Commissions' },
    { to: '/admin/reseller-withdrawals', icon: Banknote, label: 'Reseller Withdrawals' },
  ]},
  { group: 'System', items: [
    { to: '/admin/audit-logs', icon: History, label: 'Audit Logs' },
    { to: '/admin/branding', icon: Palette, label: 'Profile & Branding' },
    { to: '/admin/fees', icon: Percent, label: 'Fee Management' },
  ]},
];

const resellerNavGroups = [
  { group: 'Overview', items: [
    { to: '/reseller', icon: LayoutDashboard, label: 'Dashboard' },
  ]},
  { group: 'Network', items: [
    { to: '/reseller/merchants', icon: Store, label: 'My Merchants' },
  ]},
  { group: 'Earnings', items: [
    { to: '/reseller/commissions', icon: Coins, label: 'Commissions' },
    { to: '/reseller/withdrawals', icon: Banknote, label: 'Withdrawals' },
  ]},
];

export const DashboardLayout = ({ children }) => {
  const { user, merchant, logout, isAdmin, isReseller, api } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [fundingPending, setFundingPending] = useState(0);
  const reseller = isReseller();
  const basePath = reseller ? '/reseller' : (isAdmin() ? '/admin' : '/dashboard');
  const navGroups = reseller ? resellerNavGroups : (isAdmin() ? adminNavGroups : merchantNavGroups);

  // Live pending-funding count for admins (sidebar badge + header bell). Light polling, no sockets.
  useEffect(() => {
    if (!isAdmin() || !api) return;
    let active = true;
    const fetchCount = async () => {
      try {
        const r = await api.get('/funding/admin/pending-count');
        if (active) setFundingPending(r.data?.pending || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => { active = false; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getKYCStatusBadge = () => {
    if (!merchant) return null;
    const statusColors = {
      pending: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
      under_review: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
      approved: 'bg-green-500/20 text-green-500 border-green-500/30',
      rejected: 'bg-red-500/20 text-red-500 border-red-500/30',
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-sm border ${statusColors[merchant.kyc_status] || ''}`}>
        KYC: {merchant.kyc_status?.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setMobileOpen(false)} data-testid="sidebar-backdrop" />
      )}
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 border-r border-border/50 flex flex-col bg-background transform transition-transform duration-300 lg:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-border/50">
          <BrandLogo textClassName="text-xl" imgClassName="h-8 w-auto max-w-[150px]" />
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 px-3 py-4">
          <nav className="space-y-4">
            {navGroups.map((grp) => (
              <div key={grp.group}>
                <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {grp.group}
                </p>
                <div className="space-y-1">
                  {grp.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === basePath}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-primary/10 text-primary border border-primary/20'
                            : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                        }`
                      }
                      data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                    >
                      <item.icon className="h-4 w-4" strokeWidth={1.5} />
                      <span className="flex-1">{item.label}</span>
                      {item.to === '/admin/funding' && fundingPending > 0 && (
                        <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-yellow-500 text-[11px] font-bold text-black" data-testid="nav-funding-badge">
                          {fundingPending}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>

        </ScrollArea>

        {/* User Section */}
        <div className="p-4 border-t border-border/50">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between rounded-sm"
                data-testid="user-menu-trigger"
              >
                <div className="flex items-center gap-2 text-left">
                  <div className="h-8 w-8 rounded-sm bg-primary/20 flex items-center justify-center">
                    <span className="text-sm font-semibold text-primary">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium truncate max-w-[120px]">
                      {user?.name}
                    </span>
                    <span className="text-xs text-muted-foreground capitalize">
                      {user?.role?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={toggleTheme} data-testid="theme-toggle">
                {theme === 'dark' ? (
                  <Sun className="mr-2 h-4 w-4" />
                ) : (
                  <Moon className="mr-2 h-4 w-4" />
                )}
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 glass-header flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden rounded-sm"
              onClick={() => setMobileOpen(true)}
              data-testid="sidebar-open-btn"
            >
              <Menu className="h-5 w-5" />
            </Button>
            {!isAdmin() && merchant && (
              <>
                <span className="text-sm text-muted-foreground">
                  {merchant.business_name}
                </span>
                {getKYCStatusBadge()}
              </>
            )}
            {isAdmin() && (
              <span className="text-sm font-medium">Admin Panel</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isAdmin() && (
              <button
                onClick={() => navigate('/admin/funding')}
                className="relative rounded-sm p-2 hover:bg-accent transition-colors"
                data-testid="header-funding-bell"
                title={`${fundingPending} pending funding request(s)`}
              >
                <Bell className="h-4 w-4" />
                {fundingPending > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-[10px] font-bold text-white" data-testid="header-funding-count">
                    {fundingPending}
                  </span>
                )}
              </button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-sm"
              data-testid="header-theme-toggle"
            >
              {theme === 'dark' ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6">
          <div className="max-w-[1600px] mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};
